import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Divider,
  Button,
  Skeleton,
  Chip,
} from "@mui/material";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  getArticleDetails,
  DownloadDetailsAttachment,
} from "../api/memoHebdo.api";

function DetailField({ label, value, loading }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: "text.secondary",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      {loading ? (
        <Skeleton height={28} />
      ) : (
        <Typography fontWeight={600}>{value || "-"}</Typography>
      )}
    </Box>
  );
}

function SectionCard({ icon, title, children, rightContent }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 4,
        border: "1px solid #e5e7eb",
        height: "100%", // important
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 18,
            }}
          >
            {title}
          </Typography>
        </Stack>

        {rightContent}
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {children}
    </Paper>
  );
}

function ArticleDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  /*---------langue------*/
  const { t } = useTranslation(["Articles"]);
  const userState = useSelector((state) => state.user);
  const userLang = userState.lang;
  const isFrench = userState.lang === "FR";
  const token = userState.token;

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadArticle = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getArticleDetails(token, id);
      console.log(data);
      setArticle(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => {
    if (token) {
      loadArticle();
    }
  }, [loadArticle, token]);

  const lockedText = article?.isLocked
    ? isFrench
      ? "Oui"
      : "Yes"
    : isFrench
      ? "Non"
      : "No";

  // ================= DOWNLOAD FILE =================
  const handleDownloadFile = async (token, id, fileName) => {
    try {
      const blob = await DownloadDetailsAttachment(token, id, fileName);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box
      sx={{
        background: "#f8fafc",
        minHeight: "100vh",
        p: 4,
      }}
    >
      {/* HEADER */}

      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: 5,
          mb: 4,
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg,#f9a825,#ffca28)",
            color: "white",
            px: 4,
            py: 3,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            spacing={2}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 30,
                  fontWeight: 800,
                }}
              >
                Article Details
              </Typography>

              <Typography sx={{ opacity: 0.9 }}>
                View article information and properties
              </Typography>

              <Typography
                sx={{
                  mt: 1,
                  fontSize: 13,
                  opacity: 0.85,
                }}
              >
                {loading ? (
                  <Skeleton
                    width={120}
                    sx={{
                      bgcolor: "rgba(255,255,255,.25)",
                    }}
                  />
                ) : (
                  `Article ID : ${article?.id ?? ""}`
                )}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate(-1)}
              sx={{
                bgcolor: "rgba(255,255,255,0.18)",
                boxShadow: "none",
              }}
            >
              Back
            </Button>
          </Stack>
        </Box>
      </Paper>
      {/* ARTICLE + PROPERTIES */}
      <SectionCard
        icon={<DescriptionRoundedIcon color="primary" />}
        title="Article Information"
        rightContent={
          !loading && (
            <Box textAlign="right">
              <Typography variant="caption" color="text.secondary">
                Publication Date
              </Typography>

              <Typography fontWeight={700} fontSize={14}>
                {article?.publicationDate?.split("T")[0]}
              </Typography>
            </Box>
          )
        }
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <DetailField
              label="End Publication Date"
              value={article?.endPublicationDate?.split("T")[0]}
              loading={loading}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <DetailField
              label="Category"
              value={
                userState.lang === "FR"
                  ? article?.category?.textFr
                  : article?.category?.textEn
              }
              loading={loading}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <DetailField
              label="Signatory"
              value={article?.signatory}
              loading={loading}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <DetailField label="Locked" value={lockedText} loading={loading} />
          </Grid>

          <Grid item xs={12} md={4}>
            <DetailField
              label="Locked By"
              value={article?.lockedBy}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <Accordion
              defaultExpanded
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 3,
                boxShadow: "none",
                overflow: "hidden",
                mb: 3,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: "linear-gradient(to right, #f8fafc, #eef2f7)",
                  borderLeft: "5px solid #f9a825",
                  minHeight: 70,

                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    my: 1,
                  },

                  "&:hover": {
                    background: "linear-gradient(to right, #f1f5f9, #e5e7eb)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#1f2937",
                  }}
                >
                  {article?.titleFr}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Box
                  sx={{
                    "& img": {
                      maxWidth: "100%",
                    },
                    "& table": {
                      width: "100%",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: article?.textFr || "",
                  }}
                />

                <Divider sx={{ my: 3 }} />

                <Typography fontWeight={700} mb={2}>
                  Attachments
                </Typography>

                <Stack spacing={1}>
                  {article?.attachments
                    ?.filter((x) => x.lang === "FR")
                    ?.map((file) => (
                      <Button
                        key={file.id}
                        variant="text"
                        startIcon={<DownloadIcon />}
                        onClick={() =>
                          handleDownloadFile(token, article.id, file.fileName)
                        }
                        sx={{
                          justifyContent: "flex-start",
                        }}
                      >
                        {file.fileName}
                      </Button>
                    ))}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion
              defaultExpanded
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 3,
                boxShadow: "none",
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: "linear-gradient(to right, #f8fafc, #eef2f7)",
                  borderLeft: "5px solid #f9a825",
                  minHeight: 70,

                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    my: 1,
                  },

                  "&:hover": {
                    background: "linear-gradient(to right, #f1f5f9, #e5e7eb)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#1f2937",
                  }}
                >
                  {article?.titleEn}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Box
                  sx={{
                    "& img": {
                      maxWidth: "100%",
                    },
                    "& table": {
                      width: "100%",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: article?.textEn || "",
                  }}
                />

                <Divider sx={{ my: 3 }} />

                <Typography fontWeight={700} mb={2}>
                  Attachments
                </Typography>

                <Stack spacing={1}>
                  {article?.attachments
                    ?.filter((x) => x.lang === "EN")
                    ?.map((file) => (
                      <Button
                        key={file.id}
                        variant="text"
                        startIcon={<DownloadIcon />}
                        onClick={() =>
                          handleDownloadFile(token, article.id, file.fileName)
                        }
                        sx={{
                          justifyContent: "flex-start",
                        }}
                      >
                        {file.fileName}
                      </Button>
                    ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </SectionCard>
      <Box sx={{ height: 24 }} />

      {/* EXTERNAL DEPARTMENTS + EMPLACEMENTS */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} lg={6}>
          <SectionCard
            icon={<PeopleAltRoundedIcon color="primary" />}
            title="External Departments"
          >
            {loading ? (
              <>
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </>
            ) : (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {article?.externalDepartments?.map((department) => (
                  <Chip
                    key={department.id}
                    label={
                      userState.lang === "FR"
                        ? department.textFr
                        : department.textEn
                    }
                  />
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SectionCard
            icon={<RestaurantRoundedIcon color="primary" />}
            title="Emplacements"
          >
            {loading ? (
              <>
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </>
            ) : (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {article?.emplacements?.map((emplacement) => (
                  <Chip key={emplacement} label={emplacement} />
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
export default ArticleDetails;
